import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  CarouselSection,
  SectionTitle,
  CarouselContainer,
  CarouselTrack,
  DesktopGrid,
  Card,
  ImageSection,
  CardImage,
  TextSection,
  CardTitle,
  CardDescription,
  PaginationDots,
  Dot
} from "./Carousel.styled";
import carouselCard1 from "../../assets/images/impact-report_carousel-card-1.png";
import carouselCard2 from "../../assets/images/impact-report_carousel-card-2.png";
import carouselCard3 from "../../assets/images/impact-report_carousel-card-3.png";

const CAROUSEL_IMAGES = [carouselCard1, carouselCard2, carouselCard3];
const MIN_SWIPE_DISTANCE = 50;

const Carousel = ({
  transitionDuration = 300,
  transitionEasing = "ease-out"
}) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const slides = t("impactReport.carousel.slides", { returnObjects: true });
  const totalSlides = slides.length;

  const goToSlide = useCallback(
    index => {
      if (index >= 0 && index < totalSlides) {
        setCurrentSlide(index);
      }
    },
    [totalSlides]
  );

  const handleTouchStart = e => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = e => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

    if (isLeftSwipe && currentSlide < totalSlides - 1) {
      goToSlide(currentSlide + 1);
    }

    if (isRightSwipe && currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  };

  return (
    <CarouselSection>
      <SectionTitle>{t("impactReport.carousel.sectionTitle")}</SectionTitle>

      {/* Mobile: Carousel with swipe */}
      <CarouselContainer
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role={"region"}
        aria-label={t("impactReport.carousel.sectionTitle")}
      >
        <CarouselTrack
          currentSlide={currentSlide}
          transitionDuration={transitionDuration}
          transitionEasing={transitionEasing}
        >
          {slides.map((slide, index) => (
            <Card key={index} aria-hidden={currentSlide !== index}>
              <ImageSection>
                <CardImage
                  src={CAROUSEL_IMAGES[index]}
                  alt={slide.imageAlt}
                  loading={"lazy"}
                />
              </ImageSection>
              <TextSection>
                <CardTitle>{slide.title}</CardTitle>
                <CardDescription>{slide.description}</CardDescription>
              </TextSection>
            </Card>
          ))}
        </CarouselTrack>
      </CarouselContainer>

      <PaginationDots role={"tablist"} aria-label={"Carousel navigation"}>
        {slides.map((_, index) => (
          <Dot
            key={index}
            active={currentSlide === index}
            onClick={() => goToSlide(index)}
            role={"tab"}
            aria-selected={currentSlide === index}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </PaginationDots>

      {/* Desktop: 3-column grid */}
      <DesktopGrid>
        {slides.map((slide, index) => (
          <Card key={index}>
            <ImageSection>
              <CardImage
                src={CAROUSEL_IMAGES[index]}
                alt={slide.imageAlt}
                loading={"lazy"}
              />
            </ImageSection>
            <TextSection>
              <CardTitle>{slide.title}</CardTitle>
              <CardDescription>{slide.description}</CardDescription>
            </TextSection>
          </Card>
        ))}
      </DesktopGrid>
    </CarouselSection>
  );
};

Carousel.propTypes = {
  transitionDuration: PropTypes.number,
  transitionEasing: PropTypes.string
};

export default Carousel;
